import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import {
  PLAN_LABELS_ES,
  PLAN_TIERS,
  PLAN_PRICES_ARS,
  PLAN_COMMERCIAL_META,
  normalizePlan,
  resolvePlanPrices
} from '../../utils/planTiers';
import { PLAN_COMMERCIAL_UX_ES } from '../../utils/planCommercialUx';
import { getModulePresetForPlan } from '../../utils/planDetails';
import { loadTrialContinuationMetrics } from '../../utils/trialContinuationMetrics';
import { getTrialJourneyOverlay } from '../../utils/licenseUi';
import {
  getBillingPublicConfig,
  createLicenseMercadoPagoPreference,
  createManualPaymentRequest,
  getActiveManualPaymentRequest,
  listManualPaymentRequests,
  cancelManualPaymentRequest,
  newManualClientRequestId
} from '../../services/billing.service';
import { getMercadoPagoCheckoutUrl, goToMercadoPagoCheckout } from '../../utils/mercadopagoCheckout';
import MercadoPagoMark from '../common/MercadoPagoMark';

const CHOSEN_KEY = 'trialContinuationChosenPlan';
const MANUAL_METHODS = [
  { id: 'manual_transfer', label: 'Transferencia' },
  { id: 'manual_cash', label: 'Efectivo' },
  { id: 'manual_other', label: 'Otro (con observación)' }
];
const SLA_COPY = 'La validación puede demorar hasta 72 horas hábiles.';
const MODULE_LABELS = {
  punto_venta: 'Punto de venta',
  ventas: 'Ventas',
  stock: 'Stock',
  clientes: 'Clientes',
  compras: 'Compras',
  reportes: 'Reportes',
  transferencia: 'Transferencias',
  transferencias: 'Transferencias',
  produccion: 'Producción',
  caja: 'Caja'
};

function formatArs(n) {
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(Number(n) || 0);
  } catch {
    return `ARS ${Number(n) || 0}`;
  }
}

function highlightModules(planId) {
  const preset = getModulePresetForPlan(planId) || {};
  return Object.keys(MODULE_LABELS)
    .filter((k) => preset[k] === true)
    .slice(0, 6)
    .map((k) => MODULE_LABELS[k]);
}

/**
 * Pantalla full-screen post-trial (SPEC 001A/001B).
 * No activa licencia; chosenPlan = intención.
 */
const TrialContinuationGate = ({ lic, onLicenseReload }) => {
  const { orgId, logout } = useAuth();
  const [selected, setSelected] = useState(() => {
    const fromSession = sessionStorage.getItem(CHOSEN_KEY);
    const fromLic = lic?.chosenPlan || lic?.mercadopagoBillingPlan;
    return normalizePlan(fromSession || fromLic || 'basic');
  });
  const [metrics, setMetrics] = useState([]);
  const [metricsError, setMetricsError] = useState(false);
  const [prices, setPrices] = useState(() => ({ ...PLAN_PRICES_ARS }));
  const [billingMp, setBillingMp] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualMethod, setManualMethod] = useState('manual_transfer');
  const [manualObservation, setManualObservation] = useState('');
  const [manualReceiptRef, setManualReceiptRef] = useState('');
  const [manualPaymentRef, setManualPaymentRef] = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [manualCancelLoading, setManualCancelLoading] = useState(false);
  const [activeManual, setActiveManual] = useState(null);
  const [lastRejected, setLastRejected] = useState(null);
  const [clientRequestId] = useState(() => newManualClientRequestId());
  const approvedToastShownRef = useRef(false);
  const overlay = getTrialJourneyOverlay(lic, { phase: 'trial_expired' });

  const refreshActiveManual = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data, status } = await getActiveManualPaymentRequest();
      if (status >= 200 && status < 300 && data?.success) {
        setActiveManual(data.data?.request || null);
      }
      const listed = await listManualPaymentRequests();
      if (listed.status >= 200 && listed.status < 300 && listed.data?.success) {
        const reqs = listed.data?.data?.requests || [];
        const rejected = reqs.find((r) => r.status === 'rejected');
        setLastRejected(rejected || null);
        const approved = reqs.find((r) => r.status === 'approved');
        if (approved) {
          if (!approvedToastShownRef.current) {
            approvedToastShownRef.current = true;
            toast.success('Tu pago fue aprobado y tu plan ya está activo.', {
              toastId: `manual-approved-${approved.requestId || 'ok'}`
            });
          }
          if (typeof onLicenseReload === 'function') {
            onLicenseReload();
          }
        }
      }
    } catch {
      /* silencioso: gate sigue usable */
    }
  }, [orgId, onLicenseReload]);

  useEffect(() => {
    refreshActiveManual();
    const t = setInterval(refreshActiveManual, 20000);
    return () => clearInterval(t);
  }, [refreshActiveManual]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orgId) return;
      try {
        const r = await getDoc(doc(db, `companies/${orgId}/config/empresa`));
        if (!cancelled && r.exists()) {
          const d = r.data() || {};
          setEmpresaNombre(d.nombre_comercial || d.nombre_fantasia || d.razon_social || '');
        }
      } catch {
        /* omit */
      }
      try {
        const company = await getDoc(doc(db, 'companies', orgId));
        if (!cancelled && company.exists() && !empresaNombre) {
          setEmpresaNombre(company.data()?.name || '');
        }
      } catch {
        /* omit */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await loadTrialContinuationMetrics(orgId, lic);
        if (!cancelled) setMetrics(list);
      } catch {
        if (!cancelled) {
          setMetricsError(true);
          setMetrics([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, lic]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getBillingPublicConfig();
        if (cancelled) return;
        if (data?.success) {
          setBillingMp(data.data || null);
          const resolved = resolvePlanPrices(data.data?.planPrices, data.data?.monthlyPriceARS);
          setPrices(resolved);
        }
      } catch {
        if (!cancelled) setPrices({ ...PLAN_PRICES_ARS });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistChosenIntention = useCallback(
    async (planId) => {
      const plan = normalizePlan(planId);
      setSelected(plan);
      // Intención en cliente: rules bloquean write de license a OWNER.
      // Persistencia canónica de chosenPlan ocurre vía Admin en createLicenseMercadoPagoPreference.
      sessionStorage.setItem(CHOSEN_KEY, plan);
      if (typeof onLicenseReload === 'function') onLicenseReload();
    },
    [onLicenseReload]
  );

  const abrirMercadoPago = async () => {
    const plan = normalizePlan(selected);
    await persistChosenIntention(plan);
    if (billingMp?.mercadoPagoTokenPresent === false) {
      toast.warning('El pago con billetera virtual no está disponible en este momento. Podés usar la solicitud manual.');
      return;
    }
    setPayLoading(true);
    try {
      const { data, status } = await createLicenseMercadoPagoPreference({ plan });
      const url = getMercadoPagoCheckoutUrl(data);
      if (status === 200 && data?.success && url) {
        toast.info('Te llevamos al cobro. Al volver, tu licencia se actualizará automáticamente.', {
          autoClose: 5000
        });
        goToMercadoPagoCheckout(url);
      } else {
        toast.error(data?.message || 'No se pudo iniciar el pago. Reintentá o usá la solicitud manual.');
      }
    } catch (e) {
      toast.error(e?.message || 'Sin conexión con el servidor de pagos. Reintentá en unos minutos.');
    } finally {
      setPayLoading(false);
    }
  };

  const solicitarManual = () => {
    setManualOpen(true);
  };

  const enviarSolicitudManual = async () => {
    const plan = normalizePlan(selected);
    await persistChosenIntention(plan);
    if (manualMethod === 'manual_other' && !String(manualObservation || '').trim()) {
      toast.error('Para «Otro» la observación es obligatoria.');
      return;
    }
    setManualLoading(true);
    try {
      const { data, status } = await createManualPaymentRequest({
        planId: plan,
        paymentMethod: manualMethod,
        observation: manualObservation,
        receiptReference: manualReceiptRef || undefined,
        paymentReference: manualPaymentRef || undefined,
        clientRequestId
      });
      if (status >= 200 && status < 300 && data?.success) {
        setActiveManual(data.data?.request || null);
        toast.success(
          'Recibimos tu solicitud. Un administrador revisará el pago antes de activar tu plan.',
          { autoClose: 8000 }
        );
        setManualOpen(false);
      } else if (data?.code === 'MANUAL_PENDING_OTHER_PLAN') {
        toast.warning(data.message || 'Cancelá la solicitud anterior antes de cambiar de plan.');
        await refreshActiveManual();
      } else {
        toast.error(data?.message || 'No se pudo crear la solicitud.');
      }
    } catch (e) {
      toast.error(e?.message || 'Error al enviar la solicitud manual.');
    } finally {
      setManualLoading(false);
    }
  };

  const cancelarSolicitudManual = async () => {
    const rid = activeManual?.requestId;
    if (!rid) return;
    setManualCancelLoading(true);
    try {
      const { data, status } = await cancelManualPaymentRequest(rid);
      if (status >= 200 && status < 300 && data?.success) {
        toast.info('Solicitud cancelada.');
        setActiveManual(null);
        await refreshActiveManual();
      } else {
        toast.error(data?.message || 'No se pudo cancelar.');
      }
    } catch (e) {
      toast.error(e?.message || 'Error al cancelar.');
    } finally {
      setManualCancelLoading(false);
    }
  };

  const cards = useMemo(
    () =>
      PLAN_TIERS.map((tier) => {
        const ux = PLAN_COMMERCIAL_UX_ES[tier] || {};
        const meta = PLAN_COMMERCIAL_META[tier] || {};
        return {
          id: tier,
          title: PLAN_LABELS_ES[tier] || tier,
          price: prices[tier] ?? PLAN_PRICES_ARS[tier],
          subtitle: ux.subtitle || '',
          benefits: Array.isArray(ux.benefits) ? ux.benefits.slice(0, 4) : [],
          users: meta.includedUsers,
          branches: meta.includedBranches,
          modules: highlightModules(tier)
        };
      }),
    [prices]
  );

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      /* allow navigate */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col overflow-y-auto bg-slate-950 text-slate-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-continuation-title"
      data-testid="trial-continuation-gate"
      data-journey={overlay || 'ux_trial_expired_gate'}
    >
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-sky-300/90">0E3 POS</p>
            <h1 id="trial-continuation-title" className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
              Tu negocio ya está en marcha
            </h1>
            {empresaNombre ? (
              <p className="mt-1 text-sm text-slate-300">{empresaNombre}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href="https://0e3.app"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm font-semibold text-sky-200 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              Ayuda
            </a>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              Cerrar sesión
            </button>
          </div>
        </header>

        <section className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-5">
          <p className="text-sm leading-relaxed text-slate-200 sm:text-base">
            Toda la información que construiste durante estos 30 días sigue segura.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-slate-300 sm:text-base">
            Tus productos, clientes, ventas, compras, stock y configuraciones continúan disponibles en 0E3 POS.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-slate-300 sm:text-base">
            Elegí el plan que mejor se adapte a tu negocio y seguí trabajando exactamente desde donde lo dejaste.
          </p>
          <p className="mt-3 text-xs font-medium text-emerald-200/90 sm:text-sm">
            No se elimina absolutamente nada de tu empresa. Tus datos siguen en tu cuenta.
          </p>
        </section>

        {metrics.length > 0 ? (
          <section aria-label="Resumen de tu progreso">
            <h2 className="mb-2 text-sm font-semibold text-slate-200">Lo que ya construiste</h2>
            <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {metrics.map((m) => (
                <li
                  key={m.key}
                  className="rounded-xl border border-white/10 bg-slate-900/60 px-3 py-3"
                >
                  <p className="text-lg font-bold text-white">{m.value}</p>
                  <p className="text-xs text-slate-400">{m.label}</p>
                </li>
              ))}
            </ul>
          </section>
        ) : metricsError ? (
          <p className="text-xs text-slate-400">No pudimos cargar el resumen ahora. Podés continuar igual.</p>
        ) : null}

        <section aria-label="Planes disponibles">
          <h2 className="mb-3 text-sm font-semibold text-slate-200">Elegí tu plan</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {cards.map((card) => {
              const active = selected === card.id;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => persistChosenIntention(card.id)}
                  aria-pressed={active}
                  className={`rounded-2xl border p-4 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${
                    active
                      ? 'border-sky-400 bg-sky-500/15 shadow-lg shadow-sky-900/30'
                      : 'border-white/10 bg-slate-900/50 hover:border-white/25'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="text-lg font-bold text-white">{card.title}</h3>
                    <p className="text-sm font-semibold text-sky-200">{formatArs(card.price)}</p>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">/ mes</p>
                  <p className="mt-2 text-xs text-slate-300">{card.subtitle}</p>
                  <p className="mt-2 text-xs text-slate-400">
                    Hasta {card.users} usuarios · {card.branches} sucursal
                    {Number(card.branches) === 1 ? '' : 'es'}
                  </p>
                  <ul className="mt-3 space-y-1 text-xs text-slate-300">
                    {card.benefits.map((b) => (
                      <li key={b}>• {b}</li>
                    ))}
                  </ul>
                  {card.modules.length ? (
                    <p className="mt-3 text-[11px] text-slate-500">
                      Incluye: {card.modules.join(', ')}
                    </p>
                  ) : null}
                </button>
              );
            })}
          </div>
        </section>

        <section className="mt-auto flex flex-col gap-3 border-t border-white/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-400">
            Plan seleccionado:{' '}
            <strong className="text-slate-200">{PLAN_LABELS_ES[selected] || selected}</strong>
            {overlay === 'restricted_pending_payment' ? ' · listo para activar' : ''}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={solicitarManual}
              data-testid="manual-payment-cta"
              className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-transparent px-4 py-3 text-sm font-semibold text-slate-100 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              Solicitar activación manual
            </button>
            <button
              type="button"
              disabled={payLoading}
              onClick={abrirMercadoPago}
              data-testid="mercadopago-cta"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-3 text-sm font-bold text-slate-950 hover:bg-sky-400 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 focus-visible:ring-sky-300"
            >
              <MercadoPagoMark className="h-5 w-auto" />
              {payLoading ? 'Conectando…' : 'Continuar con Mercado Pago'}
            </button>
          </div>
        </section>

        {activeManual && activeManual.status === 'pending_review' ? (
          <section
            className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4"
            data-testid="manual-payment-pending"
            aria-live="polite"
          >
            <p className="text-sm font-semibold text-amber-100">Solicitud en revisión</p>
            <p className="mt-1 text-xs text-amber-50/90">
              Plan {activeManual.planCommercialName || PLAN_LABELS_ES[activeManual.planId] || activeManual.planId}
              {' · '}
              {formatArs(activeManual.expectedAmount)}
              {' · '}
              {activeManual.paymentMethod === 'manual_transfer'
                ? 'Transferencia'
                : activeManual.paymentMethod === 'manual_cash'
                  ? 'Efectivo'
                  : 'Otro'}
            </p>
            <p className="mt-2 text-xs text-amber-50/80">{SLA_COPY}</p>
            {activeManual.needsMoreInfo && activeManual.infoRequestMessage ? (
              <p className="mt-2 rounded-lg bg-black/20 px-3 py-2 text-xs text-amber-50" data-testid="manual-info-needed">
                Necesitamos información adicional para validar el pago: {activeManual.infoRequestMessage}
              </p>
            ) : null}
            <p className="mt-2 text-xs text-slate-300">
              Un pago aprobado con Mercado Pago habilita automáticamente y reemplaza esta solicitud.
            </p>
            <button
              type="button"
              disabled={manualCancelLoading}
              onClick={cancelarSolicitudManual}
              data-testid="manual-payment-cancel"
              className="mt-3 rounded-lg border border-white/20 px-3 py-2 text-xs font-semibold text-slate-100 hover:bg-white/10 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              {manualCancelLoading ? 'Cancelando…' : 'Cancelar solicitud'}
            </button>
          </section>
        ) : null}

        {lastRejected && !activeManual ? (
          <section
            className="rounded-2xl border border-rose-400/30 bg-rose-500/10 p-4"
            data-testid="manual-payment-rejected"
            aria-live="polite"
          >
            <p className="text-sm font-semibold text-rose-100">Solicitud rechazada</p>
            <p className="mt-1 text-xs text-rose-50/90">
              {lastRejected.rejectionReason || 'El administrador no pudo validar el pago.'}
            </p>
            <p className="mt-2 text-xs text-slate-300">
              Podés crear una nueva solicitud o continuar con Mercado Pago.
            </p>
          </section>
        ) : null}

        {manualOpen ? (
          <div
            className="fixed inset-0 z-[90] flex items-end justify-center bg-black/70 p-4 sm:items-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="manual-payment-title"
            data-testid="manual-payment-panel"
          >
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/15 bg-slate-900 p-5 shadow-2xl">
              <h2 id="manual-payment-title" className="text-lg font-bold text-white">
                Solicitud de activación manual
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                Elegiste el plan{' '}
                <strong className="text-white">{PLAN_LABELS_ES[normalizePlan(selected)]}</strong> por{' '}
                <strong className="text-white">{formatArs(prices[normalizePlan(selected)])}</strong>.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                Podés pagar por transferencia, efectivo u otro medio acordado. Cuando hayas realizado el pago,
                enviá la solicitud para que un administrador pueda verificarlo.
              </p>
              <p className="mt-2 text-xs font-medium text-amber-200/90">{SLA_COPY}</p>
              <p className="mt-2 text-xs text-slate-400">
                Podés indicar una referencia de comprobante ahora o enviarla cuando el administrador lo solicite.
                Esto no activa tu cuenta.
              </p>

              <fieldset className="mt-4">
                <legend className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Medio de pago
                </legend>
                <div className="mt-2 flex flex-col gap-2">
                  {MANUAL_METHODS.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 text-sm text-slate-200">
                      <input
                        type="radio"
                        name="manual-method"
                        value={m.id}
                        checked={manualMethod === m.id}
                        onChange={() => setManualMethod(m.id)}
                      />
                      {m.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="mt-4 block text-xs font-semibold text-slate-400">
                Referencia de pago (opcional)
                <input
                  type="text"
                  value={manualPaymentRef}
                  onChange={(e) => setManualPaymentRef(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/15 bg-slate-950 px-3 py-2 text-sm text-white"
                  maxLength={200}
                />
              </label>

              <label className="mt-3 block text-xs font-semibold text-slate-400">
                Referencia de comprobante (opcional · texto corto)
                <input
                  type="text"
                  value={manualReceiptRef}
                  onChange={(e) => setManualReceiptRef(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/15 bg-slate-950 px-3 py-2 text-sm text-white"
                  maxLength={500}
                  placeholder="Ej. transferencia CBU … / nº operación"
                />
              </label>

              <label className="mt-3 block text-xs font-semibold text-slate-400">
                Observación{manualMethod === 'manual_other' ? ' (obligatoria)' : ' (opcional)'}
                <textarea
                  value={manualObservation}
                  onChange={(e) => setManualObservation(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/15 bg-slate-950 px-3 py-2 text-sm text-white"
                  rows={3}
                  maxLength={2000}
                />
              </label>

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setManualOpen(false)}
                  className="rounded-xl border border-white/20 px-4 py-2.5 text-sm font-semibold text-slate-100 hover:bg-white/10"
                >
                  Volver
                </button>
                <button
                  type="button"
                  disabled={manualLoading}
                  onClick={enviarSolicitudManual}
                  data-testid="manual-payment-submit"
                  className="rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-slate-950 hover:bg-emerald-400 disabled:opacity-60"
                >
                  {manualLoading ? 'Enviando…' : 'Ya realicé el pago'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
export default TrialContinuationGate;
